/**
 * useZoom Hook
 * 
 * Manages zoom state with smooth animations.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseZoomOptions {
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
}

export interface UseZoomReturn {
  zoom: number;
  MIN_ZOOM: number;
  MAX_ZOOM: number;
  setZoom: (zoom: number) => void;
  handleWheel: (e: React.WheelEvent, shiftKeyForZoom: boolean, isPanEnabled: boolean) => void;
  currentZoomRef: React.MutableRefObject<number>;
}

// ============================================================================
// Hook
// ============================================================================

export function useZoom(options: UseZoomOptions = {}): UseZoomReturn {
  const {
    minZoom = 1,
    maxZoom = 5,
    initialZoom = 1,
  } = options;

  const [zoom, setZoomState] = useState(initialZoom);
  const currentZoomRef = useRef(initialZoom);
  const zoomUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync ref with state for smooth animations
  useEffect(() => {
    currentZoomRef.current = zoom;
  }, [zoom]);

  // Wrapper for setZoom that ensures zoom is within valid range
  const setZoom = useCallback((newZoom: number) => {
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
    setZoomState(clampedZoom);
  }, [minZoom, maxZoom]);

  // Handle mouse wheel for zoom (with shift) or pan (without shift)
  const handleWheel = useCallback((
    e: React.WheelEvent,
    shiftKeyForZoom: boolean = true,
    isPanEnabled: boolean = true
  ) => {
    // Allow browser zoom (Ctrl/Cmd + scroll) to work normally
    if (e.ctrlKey || e.metaKey) {
      return; // Let browser handle Ctrl/Cmd + scroll for page zoom
    }

    e.preventDefault();

    if (shiftKeyForZoom && e.shiftKey) {
      // ZOOM MODE (Shift + Scroll)
      // Calculate zoom delta with smoother, more responsive scaling
      const zoomFactor = 0.02; // Fine-grained zoom steps
      const delta = e.deltaY > 0 ? 1 - zoomFactor : 1 + zoomFactor;
      let newZoom = zoom * delta;

      // Clamp to valid range
      newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

      // Use requestAnimationFrame for smooth, immediate updates
      // Cancel any pending timeout
      if (zoomUpdateTimeoutRef.current) {
        clearTimeout(zoomUpdateTimeoutRef.current);
        zoomUpdateTimeoutRef.current = null;
      }

      // Update ref immediately for smooth visual feedback
      currentZoomRef.current = newZoom;

      // Update state (triggers re-render via requestAnimationFrame)
      setZoom(newZoom);
    } else if (!e.shiftKey && isPanEnabled) {
      // PAN MODE handled by parent (not in this hook)
      // Just return the event data so parent can handle pan
      // This is a no-op in the hook itself
    }
  }, [zoom, setZoom, minZoom, maxZoom]);

  return {
    zoom,
    MIN_ZOOM: minZoom,
    MAX_ZOOM: maxZoom,
    setZoom,
    handleWheel,
    currentZoomRef,
  };
}

